import { useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useLinksContext } from '../../context/LinksContext';
import {
  HomeIcon,
  InfoIcon,
  SupportIcon,
  TransportIcon,
} from '../../components/MenusIcons/MenusIcons';
import { useTasklist } from '@/context/TasklistContext';
import { useSchool } from '@context/SchoolContext';
import { useSelectedStudent } from '@context/SelectedStudentContext';
import { useSkeletonLoader } from '@/hooks/useSkeletonLoader/useSkeletonLoader';
import { BadgeSkeleton } from '@components/Skeletons/Skeletons';
import { resolveSidebarPrimaryNavKey } from '@/utils/tasklistPath';
import { useFilteredTasklistTasks } from '@/hooks/useFilteredTasklistTasks/useFilteredTasklistTasks';
import { useOverviewContext } from '@/context/OverviewContext';
import { isTeacherPreview } from '@/utils/toolSettingsUtils';

interface SidebarMenuProps {
  onItemClick?: () => void;
}

const SidebarMenu: React.FC<SidebarMenuProps> = ({ onItemClick }) => {
  useLinksContext();
  const { userRoleVal } = useOverviewContext();
  const teacherPreview = isTeacherPreview(userRoleVal);
  const { tasklistData, learningCenterTasks } = useTasklist();
  const { visibleHeroTasks } = useFilteredTasklistTasks();
  const location = useLocation();

  const primaryNav = useMemo(() => {
    const heroTasksForSidebar = tasklistData.map((r) => ({
      pageId: r.pageId,
      pathSegment: r.pathSegment,
      checklistName: r.checklistName,
    }));
    const learningCenterForSidebar = learningCenterTasks.map((t) => ({
      pageId: t.pageId,
      pathSegment: t.pathSegment,
    }));

    return resolveSidebarPrimaryNavKey(
      location.pathname,
      heroTasksForSidebar,
      learningCenterForSidebar
    );
  }, [location.pathname, tasklistData, learningCenterTasks]);

  const { selectedStudent } = useSelectedStudent();
  const { data: schoolData, error: schoolError } = useSchool();
  const showSkeleton = useSkeletonLoader({
    schoolData,
    schoolError,
    selectedStudent,
    retryDelay: 3000,
    initialDelay: 2000,
    maxRetries: 2,
  });

  /** Match Overview hero: visible per page JSON + completion from API merge in `heroTasks`. */
  const incompleteHomeTaskCount = selectedStudent
    ? visibleHeroTasks.filter((t) => !t.completed).length
    : 0;

  const navClass = ({ isActive }: { isActive: boolean }) =>
    isActive ? 'menuItemAnchor active' : 'menuItemAnchor';

  return (
    <>
      <nav className="primaryMenu">
        <ul className="menu">
          <li className="menuItem">
            <NavLink
              to="/overview"
              className={({ isActive }) =>
                navClass({ isActive: isActive || primaryNav === 'home' })
              }
              onClick={onItemClick}
              end
            >
              <span className="menu-icon">
                <HomeIcon />
              </span>
              <span className="menuItemLabel">Home</span>
              {showSkeleton ? (
                <BadgeSkeleton />
              ) : (
                !teacherPreview &&
                visibleHeroTasks.length > 0 &&
                incompleteHomeTaskCount > 0 && (
                  <span className="badge">{incompleteHomeTaskCount}</span>
                )
              )}
            </NavLink>
          </li>
          <li className="menuItem">
            <NavLink
              to="/learning-center"
              end
              className={({ isActive }) =>
                navClass({
                  isActive: isActive || primaryNav === 'learning-center',
                })
              }
              onClick={onItemClick}
            >
              <span className="menu-icon">
                <InfoIcon />
              </span>
              <span className="menuItemLabel">Learning Center</span>
            </NavLink>
          </li>
          <li className="menuItem">
            <NavLink to="/classroom-materials-tracker" className={navClass} onClick={onItemClick}>
              <span className="menu-icon">
                <TransportIcon />
              </span>
              <span className="menuItemLabel">Classroom Materials Tracker</span>
            </NavLink>
          </li>
          <li className="menuItem menu-icon-support">
            <NavLink to="/help-and-support" className={navClass} onClick={onItemClick}>
              <span className="menu-icon">
                <SupportIcon />
              </span>
              <span className="menuItemLabel">Help & Support</span>
            </NavLink>
          </li>
        </ul>
      </nav>
    </>
  );
};

export default SidebarMenu;
